import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { ONE_TAP_NEW_LEAD_KEY } from '@components/GoogleOneTap/constants';
import useElementsHeight from '@hooks/useElementsHeight';
import useWindowSize from '@hooks/useWindowSize';
import { edit_user_email_settings_path } from '@routes';

interface OneTapWelcomeBannerProps {
  isLoggedIn: boolean;
  shouldShowScrolledNav?: boolean;
}

const hasNewLeadFlag = () => {
  try {
    return window.sessionStorage.getItem(ONE_TAP_NEW_LEAD_KEY) === '1';
  } catch {
    return false;
  }
};

const clearNewLeadFlag = () => {
  try {
    window.sessionStorage.removeItem(ONE_TAP_NEW_LEAD_KEY);
  } catch {
    // sessionStorage unavailable; nothing to clear
  }
};

// Disclosure shown after a new lead is created through Google One Tap. Client-only: the flag
// lives in sessionStorage (set by GoogleOneTap), so the banner lasts the tab session. It is
// intentionally not dismissible. Rendered after hydration, so it never ships in cached HTML.
// In the scrolled-nav state the nav is `position: fixed` at the top of the viewport, so this
// in-flow banner would sit underneath it. Like AITopBanner, shift it down by the fixed nav's
// height (plus any fixed awareness bar), measured into its own CSS variable.
const OneTapWelcomeBanner = ({
  isLoggedIn,
  shouldShowScrolledNav = false,
}: OneTapWelcomeBannerProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const { width: windowWidth } = useWindowSize();

  useElementsHeight({
    selectors: ['.top-sticky-banner', '#global-nav-wrapper'],
    isScrollStyle: shouldShowScrolledNav && isVisible,
    dependency: [windowWidth],
    cssVariable: 'one-tap-banner-offset',
  });

  useEffect(() => {
    if (!isLoggedIn) {
      // Don't let a later login by another account in this tab inherit the flag.
      clearNewLeadFlag();
      setIsVisible(false);
      return;
    }
    setIsVisible(hasNewLeadFlag());
  }, [isLoggedIn]);

  if (!isVisible) return null;

  return (
    <aside
      role="status"
      data-testid="one-tap-welcome-banner"
      className={clsx('w-full sm:px-28 print:!hidden', {
        'relative top-[var(--one-tap-banner-offset,0px)]': shouldShowScrolledNav,
      })}
    >
      <div className="mx-auto max-w-container-lg bg-primary-magenta px-16 py-12 text-center font-suisse-intl text-overline-md font-normal text-white sm:text-button-md">
        <p className="m-0">
          You&apos;re now subscribed to our newsletters. Manage them anytime in{' '}
          <a href={edit_user_email_settings_path()} className="text-white underline">
            Settings
          </a>
          .{' '}
        </p>
        <p className="m-0">
          Continue to subscribe for reporting from inside the story, before the rest of the market
          catches up.
        </p>
      </div>
    </aside>
  );
};

export default OneTapWelcomeBanner;
